import { Link } from 'react-router-dom';
import { ContributionCalculator } from '@components/nib/ContributionCalculator';
import { NIB_CONSTANTS } from '@/types';
import {
  ArrowLeftIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export const NIBCalculator = () => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BS', {
      style: 'currency',
      currency: 'BSD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/nib-c10"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Submissions
          </Link>

          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900">
                NIB Contribution Calculator
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                Calculate employee and employer NIB contributions based on
                current Bahamas rates
              </p>
            </div>
          </div>
        </div>

        {/* Educational Banner */}
        <div className="mb-6 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-base font-semibold text-primary-900">
                Understanding NIB Contributions
              </h2>
              <p className="mt-1 text-sm text-primary-800">
                The National Insurance Board (NIB) requires employers to
                contribute to social security for all employees. This calculator
                helps you understand how contributions are computed, including
                wage truncation, weekly caps, and special employee types.
              </p>
              <ul className="mt-3 text-sm text-primary-800 space-y-1">
                <li>
                  • Wages are <strong>truncated</strong> to remove cents before
                  calculation
                </li>
                <li>
                  • A <strong>weekly cap</strong> of{' '}
                  {formatCurrency(NIB_CONSTANTS.WEEKLY_WAGE_CAP)} limits
                  insurable wages
                </li>
                <li>
                  • <strong>Summer students</strong> have reduced employer
                  contribution rates
                </li>
                <li>
                  • <strong>Hospitality workers</strong> include gratuity at
                  9.8%
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calculator - Takes 2 columns */}
          <div className="lg:col-span-2">
            <ContributionCalculator />
          </div>

          {/* Info Sidebar - Takes 1 column */}
          <div className="space-y-6">
            {/* NIB Rates Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white">
                <div className="flex items-center gap-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Current NIB Rates
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-gray-600">Employee Rate</dt>
                    <dd className="mt-1 text-2xl font-bold text-gray-900">
                      {formatPercentage(NIB_CONSTANTS.EMPLOYEE_RATE)}
                    </dd>
                    <dd className="mt-0.5 text-xs text-gray-500">
                      Deducted from employee wages
                    </dd>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <dt className="text-sm text-gray-600">Employer Rate</dt>
                    <dd className="mt-1 text-2xl font-bold text-gray-900">
                      {formatPercentage(NIB_CONSTANTS.EMPLOYER_RATE)}
                    </dd>
                    <dd className="mt-0.5 text-xs text-gray-500">
                      Paid by employer
                    </dd>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <dt className="text-sm text-gray-600">
                      Gratuity Rate (Hospitality)
                    </dt>
                    <dd className="mt-1 text-2xl font-bold text-primary-600">
                      {formatPercentage(NIB_CONSTANTS.GRATUITY_RATE)}
                    </dd>
                    <dd className="mt-0.5 text-xs text-gray-500">
                      Applied to tips and gratuities
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Wage Caps Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-white">
                <h3 className="text-lg font-medium text-gray-900">
                  Insurable Wage Caps
                </h3>
              </div>
              <div className="p-6">
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-gray-600">
                      Weekly Wage Maximum
                    </dt>
                    <dd className="mt-1 text-2xl font-bold text-amber-600">
                      {formatCurrency(NIB_CONSTANTS.WEEKLY_WAGE_CAP)}
                    </dd>
                    <dd className="mt-0.5 text-xs text-gray-500">
                      Per week cap on insurable wages
                    </dd>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <dt className="text-sm text-gray-600">
                      Monthly Wage Maximum
                    </dt>
                    <dd className="mt-1 text-2xl font-bold text-amber-600">
                      {formatCurrency(NIB_CONSTANTS.MONTHLY_WAGE_CAP)}
                    </dd>
                    <dd className="mt-0.5 text-xs text-gray-500">
                      Monthly cap (≈4.33 weeks)
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 p-3 bg-amber-50 rounded-md">
                  <p className="text-xs text-amber-800">
                    Wages exceeding these caps are not subject to NIB
                    contributions. The system automatically applies these limits
                    when processing payroll.
                  </p>
                </div>
              </div>
            </div>

            {/* Special Rules Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                <h3 className="text-lg font-medium text-gray-900">
                  Special Employee Types
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Summer Students */}
                <div className="flex items-start gap-3">
                  <AcademicCapIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      Summer Students
                    </h4>
                    <p className="mt-1 text-xs text-gray-600">
                      Employer rate reduced to{' '}
                      <strong>
                        {formatPercentage(
                          NIB_CONSTANTS.SUMMER_STUDENT_EMPLOYER_RATE
                        )}
                      </strong>{' '}
                      for students employed during summer months. Employee rate
                      remains at {formatPercentage(NIB_CONSTANTS.EMPLOYEE_RATE)}
                      .
                    </p>
                  </div>
                </div>

                {/* Retirees */}
                <div className="flex items-start gap-3 pt-4 border-t border-gray-200">
                  <UserGroupIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      Retirees
                    </h4>
                    <p className="mt-1 text-xs text-gray-600">
                      Special calculation rules apply for employees receiving
                      retirement benefits. Contributions required only if wages
                      exceed ${NIB_CONSTANTS.RETIREE_THRESHOLD} per month.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            This calculator uses current NIB rates as of 2024. For official
            guidance and the most up-to-date information, please visit{' '}
            <a
              href="https://nib.gov.bs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              NIB.gov.bs
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
